/**
 * # as-portal
 *
 * Moves its child content into a matching `<as-portal-target>` element.
 * Matched by the `target` attribute on the portal and `name` attribute on the target.
 *
 * ## Attributes
 * - `target`: The name of the portal target to move children into
 *
 * ## Usage
 * ```html
 * <!-- In app index.html -->
 * <as-portal target="welcome">
 *   <p>Welcome to my app!</p>
 * </as-portal>
 *
 * <!-- In inspector (rendered by as-app) -->
 * <as-portal-target name="welcome"></as-portal-target>
 * ```
 */
import { AsPortalTarget } from './as-portal-target.ts';

export class AsPortal extends HTMLElement {
  static get observedAttributes() {
    return ['target'];
  }

  private moved = false;
  private boundOnTargetReady: (e: Event) => void;

  constructor() {
    super();
    this.boundOnTargetReady = (e: Event) => this.onTargetReady(e as CustomEvent);
  }

  connectedCallback() {
    // Hide by default - content only becomes visible inside the target
    this.style.display = 'none';
    window.addEventListener('as-portal-target-ready', this.boundOnTargetReady);
    // Try immediately in case target already exists
    this.tryMove();
  }

  disconnectedCallback() {
    window.removeEventListener('as-portal-target-ready', this.boundOnTargetReady);
  }

  private onTargetReady(e: CustomEvent) {
    if (e.detail.name === this.getAttribute('target')) {
      this.tryMove();
    }
  }

  private tryMove() {
    if (this.moved) return;
    const targetName = this.getAttribute('target');
    if (!targetName) return;

    // Search all shadow roots and light DOM for the target
    const target = this.findTarget(targetName);
    if (!target) return;

    while (this.childNodes.length > 0) {
      target.appendChild(this.childNodes[0]);
    }
    this.moved = true;
    this.style.display = 'none';
  }

  private findTarget(name: string): AsPortalTarget | null {
    const app = this.closest('as-app') || document.querySelector('as-app');
    if (app) {
      // Search in app shadow DOM first
      if (app.shadowRoot) {
        const found = this.searchInRoot(app.shadowRoot, name);
        if (found) return found;
      }
      // Then light DOM
      const foundInLight = this.searchInRoot(app as any, name);
      if (foundInLight) return foundInLight;
    }
    // Fallback to document search
    return this.searchInRoot(document, name);
  }

  private searchInRoot(root: Document | ShadowRoot, name: string): AsPortalTarget | null {
    const selector = `as-portal-target[name="${name}"]`;
    const direct = root.querySelector(selector);
    if (direct) return direct as AsPortalTarget;

    const allEls = root.querySelectorAll('*');
    for (const el of allEls) {
      if (el.shadowRoot) {
        const found = this.searchInRoot(el.shadowRoot, name);
        if (found) return found;
      }
    }
    return null;
  }
}

if (!customElements.get('as-portal')) {
  customElements.define('as-portal', AsPortal);
}
